import React, { Suspense, useEffect, useState } from "react"
import { HMSRoomProvider } from "@100mslive/react-sdk"
import { AppData } from "./components/app-data/app-data"
import { ErrorBoundary } from "./components/error-boundary"
import FullPageProgress from "./components/full-page-progress"
import { Init } from "./components/init/init"
import { KeyboardHandler } from "./components/input/keyboard-input-manager"
import { Notifications } from "./components/notifications"
import PostLeave from "./components/post-leave"
import { hmsActions, hmsNotifications, hmsStats, hmsStore } from "./hms"
import { WebinarAppContext, useWebinarAppContext } from "./state"
import { Box } from "./components/shared/box"
import { ToastContextProvider } from "./components/hooks/useToast"

const Conference = React.lazy(() => import("./components/conference"))
const PreviewScreen = React.lazy(() => import("./components/preview-screen"))

const defaultTokenEndpoint = process.env.GATSBY_WEBINAR_TOKEN_ENDPOINT

const EdtechComponent = ({
  initialRoomId,
  themeConfig: { logo = "", metadata = "", recordingUrl = "" },
  policyConfig = {},
  getDetails = () => {},
  authTokenByRoomCodeEndpoint = "",
}) => {
  const { view, goToMeeting, goToStreaming } = useWebinarAppContext()
  const [roomId] = useState(initialRoomId)

  useEffect(() => {
    getDetails()
  }, [getDetails, roomId])

  return (
    <ToastContextProvider>
      <ErrorBoundary>
        <HMSRoomProvider
          actions={hmsActions}
          store={hmsStore}
          notifications={hmsNotifications}
          stats={hmsStats}
        >
          <AppData
            appDetails={metadata}
            policyConfig={policyConfig}
            recordingUrl={recordingUrl}
            logo={logo}
            tokenEndpoint={defaultTokenEndpoint || authTokenByRoomCodeEndpoint}
          />
          <Init />
          <Box>
            <Notifications />
            <KeyboardHandler />

            {view === "preview" && (
              <Suspense fallback={<FullPageProgress />}>
                <PreviewScreen
                  roomId={roomId}
                  onGoToMeeting={goToMeeting}
                  authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint}
                />
              </Suspense>
            )}

            {view === "meeting" && (
              <Suspense fallback={<FullPageProgress />}>
                <Conference roomId={roomId} onGoToStreaming={goToStreaming} />
              </Suspense>
            )}

            {view === "leave" && <PostLeave />}
          </Box>
        </HMSRoomProvider>
      </ErrorBoundary>
    </ToastContextProvider>
  )
}

const App = ({ initialRoomId, initialRole }) => (
  <EdtechComponent
    initialRoomId={initialRoomId}
    initialRole={initialRole}
    themeConfig={{
      aspectRatio: "1-1",
      metadata: "{}",
    }}
  />
)

export default ({
  roomCode,
  setWebinarAnnouncement,
  promotions,
  startDate,
  title,
  trackingId,
  durationInMinutes,
  emailList,
  emailSource,
  email,
}) => (
  <WebinarAppContext
    initialRoomId={roomCode}
    promotions={promotions}
    startDate={startDate}
    setWebinarAnnouncement={setWebinarAnnouncement}
    trackingId={trackingId}
    title={title}
    durationInMinutes={durationInMinutes}
    emailSource={emailSource}
    emailList={emailList}
    email={email}
  >
    <App />
  </WebinarAppContext>
)
